/*
 * Nội dung tĩnh do admin sửa trên Content Hub (content-service, type `site_page`).
 *
 * Mô hình "override trên nền code": code vẫn là nội dung mặc định cho 27 ngôn ngữ;
 * CMS chỉ lưu phần đã sửa. `npm run cms:pull` kéo bản đã publish về
 * `src/redesign/cmsSiteContent.generated.json` (commit vào git) → generator/worker
 * merge lúc build → nội dung chỉ lên prod khi deploy lại.
 *
 * Form admin KHÔNG khai báo field tay: `deriveCmsFields` sinh field từ chính nội dung
 * mặc định (chuỗi → ô nhập, mảng chuỗi → "mỗi dòng một mục", mảng object → danh sách),
 * registry chỉ khai báo trang nào, nằm ở đâu, mảng nào cố định, key nào không cho sửa.
 *
 * Module CommonJS thuần (không import gì khác) để dùng được ở: script Node (pull,
 * generate runtime locales), worker Cloudflare, Next SSR và bundle client.
 * Plan: docs/cms-static-content-plan-2026-10-07.md.
 */

const SNAPSHOT_SITE_KEY = "qrcode-gen";
const SNAPSHOT_FILE = "src/redesign/cmsSiteContent.generated.json";

/* ------------------------------------------------------------------ */
/* Registry                                                            */
/* ------------------------------------------------------------------ */

// Key không bao giờ cho sửa (id, icon, dữ liệu giao thức…). Link/đường dẫn loại theo GIÁ TRỊ
// (isLinkLikeValue) vì key như `url` cũng là tên sản phẩm (ecosystem.products.url).
const EXCLUDED_KEYS = new Set([
  "href", "route", "id", "icon", "type", "kind", "key", "number", "source", "encoded",
  "editorialLayout", "visual", "generatorTab", "src", "image", "seoTitle", "features"
]);
const isLinkLikeValue = (value) => /^(https?:\/\/|\/|mailto:|tel:|#)/i.test(String(value).trim());

// Nhãn tiếng Việt cho key hay gặp (fallback: tự tách camelCase)
const KEY_LABELS = {
  title: "Tiêu đề", description: "Mô tả", eyebrow: "Nhãn nhỏ", intro: "Dẫn nhập", summary: "Tóm tắt",
  q: "Câu hỏi", a: "Trả lời", question: "Câu hỏi", answer: "Trả lời", label: "Nhãn", value: "Giá trị",
  name: "Tên", tagline: "Khẩu hiệu", note: "Ghi chú", primary: "Nút chính", secondary: "Nút phụ",
  cta: "Lời kêu gọi (CTA)", ctaTitle: "CTA — tiêu đề", ctaDescription: "CTA — mô tả", ctaPrimary: "CTA — nút chính",
  ctaSecondary: "CTA — nút phụ", ctaButton: "CTA — nút", ctaLabel: "CTA — nhãn nút",
  faqs: "Câu hỏi thường gặp", faq: "Câu hỏi thường gặp", faqTitle: "FAQ — tiêu đề", faqDescription: "FAQ — dẫn nhập",
  faqLink: "FAQ — chữ link liên hệ", steps: "Các bước", benefits: "Lợi ích", useCases: "Tình huống sử dụng",
  useCasesTitle: "Tình huống sử dụng — tiêu đề", useCasesDescription: "Tình huống sử dụng — mô tả",
  pros: "Ưu điểm", cons: "Nhược điểm", tips: "Mẹo", points: "Điểm chính", troubleshooting: "Khắc phục sự cố",
  facts: "Điểm nhanh (chip)", demoTitle: "Demo — tiêu đề", demoLabels: "Demo — nhãn ô nhập", demoStatus: "Demo — trạng thái",
  whyTitle: "Vì sao — tiêu đề H2", whyDescription: "Vì sao — mô tả", howItWorksEyebrow: "Cách hoạt động — nhãn nhỏ",
  howItWorksTitle: "Cách hoạt động — tiêu đề H2", howItWorksDescription: "Cách hoạt động — mô tả",
  technicalGuide: "Hướng dẫn kỹ thuật", creationGuide: "Hướng dẫn tạo mã", example: "Ví dụ", input: "Dữ liệu vào",
  result: "Kết quả khi quét", navLabel: "Tên trong menu", hero: "Phần đầu trang", commitments: "Cam kết",
  commitmentsTitle: "Tiêu đề khối cam kết", toolbox: "Khối loại QR", qrTypes: "8 loại QR (thứ tự gắn với trang sản phẩm)",
  dynamicStatic: "Khối QR động & tĩnh (bố cục SEO)", customize: "Khối tuỳ chỉnh (bố cục SEO)", business: "Khối công cụ doanh nghiệp (bố cục SEO)",
  ecosystem: "Khối hệ sinh thái sản phẩm", products: "Sản phẩm", applications: "Khối tình huống sử dụng",
  platform: "Khối nền tảng", analytics: "Thống kê quét", process: "Khối các bước tạo mã", tag: "Nhãn",
  badge: "Nhãn góc", meta: "Dòng phụ", insights: "Nhận xét", total: "Tổng", periodRange: "Khoảng thời gian",
  periodLink: "Link ví dụ", plans: "Gói", items: "Mục", compareHeaders: "Bảng so sánh — tiêu đề cột",
  compareEyebrow: "So sánh — nhãn nhỏ", compareTitle: "So sánh — tiêu đề", reasons: "Lý do chọn",
  reasonsEyebrow: "Lý do chọn — nhãn nhỏ", reasonsTitle: "Lý do chọn — tiêu đề", reasonsDescription: "Lý do chọn — mô tả",
  popular: "Nhãn gói phổ biến", perForever: "Chữ 'trọn đời'", perMonth: "Chữ 'mỗi tháng'", register: "Nút đăng ký",
  responseNote: "Ghi chú thời gian phản hồi", formTitle: "Form — tiêu đề", formDescription: "Form — mô tả",
  fullName: "Nhãn họ tên", fullNamePlaceholder: "Gợi ý họ tên", email: "Nhãn email", emailPlaceholder: "Gợi ý email",
  phone: "Nhãn điện thoại", phonePlaceholder: "Gợi ý điện thoại", message: "Nhãn nội dung", messagePlaceholder: "Gợi ý nội dung",
  topicLabel: "Nhãn chủ đề", topics: "Chủ đề", submit: "Nút gửi", sending: "Đang gửi", success: "Thông báo thành công",
  error: "Thông báo lỗi", companyDescription: "Mô tả công ty", addressLabel: "Nhãn địa chỉ", phoneLabel: "Nhãn điện thoại",
  emailLabel: "Nhãn email", facebookLabel: "Nhãn Facebook", breadcrumb: "Breadcrumb", breadcrumbHome: "Breadcrumb — trang chủ",
  titleAccent: "Tiêu đề — phần nhấn", heroStats: "Số liệu đầu trang", qrTags: "Tag QR", tocTitle: "Mục lục — tiêu đề",
  chapterLabel: "Nhãn chương", groups: "Nhóm", tocGroups: "Mục lục — nhóm", tocCta: "Mục lục — CTA", dataTags: "Tag dữ liệu",
  staticCard: "Thẻ QR tĩnh", dynamicCard: "Thẻ QR động", createTitle: "Tạo mã — tiêu đề", createSteps: "Tạo mã — các bước",
  correctionLevels: "Mức sửa lỗi", printDistance: "Khoảng cách in", printDistanceNote: "Ghi chú khoảng cách in",
  sections: "Các mục", heading: "Tiêu đề mục", paragraphs: "Đoạn văn", bullets: "Gạch đầu dòng", mistakes: "Lỗi thường gặp",
  ctaPlans: "CTA — gói", ctaPayment: "CTA — thanh toán", featured: "Nhãn nổi bật", latest: "Nhãn mới nhất", loading: "Đang tải",
  empty: "Trống", readMore: "Đọc thêm", loadMore: "Tải thêm", imageAlt: "Alt ảnh", seo: "SEO", ui: "Chuỗi giao diện",
  highlights: "Điểm nổi bật", tool: "Công cụ", onlineGuide: "Hướng dẫn quét online", deviceGuides: "Hướng dẫn theo thiết bị",
  safety: "An toàn", checks: "Kiểm tra", relatedTitle: "Liên quan — tiêu đề", relatedLinksTitle: "Liên quan — tiêu đề",
  scannerUi: "Giao diện máy quét", topicNavTitle: "Điều hướng chủ đề", displayTitle: "Tiêu đề hiển thị (H1)",
  shortTitle: "Tiêu đề ngắn", detailTitle: "Tiêu đề chi tiết", homeLabel: "Nhãn trang chủ", breadcrumbLabel: "Nhãn breadcrumb",
  relatedAriaLabel: "Aria label liên quan", alt: "Alt", process: "Quy trình", types: "Loại", home: "Trang chủ"
};

/**
 * Mỗi entry: pageKey (slug trong CMS), route chuẩn (path không tiền tố ngôn ngữ), nguồn nội dung:
 * - contentPath: vị trí trong content redesign (getRedesignContent) — merge trong buildRedesignContent.
 * - source: "scan" | "help" | "legal" — module riêng, merge trong module đó (xem applyCmsPageData).
 * - null cả hai: chỉ sửa thẻ SEO.
 * fieldConfig: fixedPaths (mảng cố định số phần tử — web map theo index), excludePaths, labels.
 * Trang sản phẩm sinh động từ routes (xem listCmsPages).
 */
const HOME_FIELD_CONFIG = {
  fixedPaths: ["commitments", "qrTypes", "useCases", "steps", "features", "analytics.insights"],
  excludePaths: ["analytics.rows", "ecosystem.products.*.demo"],
  labels: {
    "hero.title": "H1 trang chủ", "hero.description": "Sapo dưới H1",
    "ecosystem.products.*.facts": "Điểm nhanh", "faqs": "Câu hỏi thường gặp (FAQ JSON-LD)"
  }
};
const PRODUCT_FIELD_CONFIG = {
  fixedPaths: ["facts", "demoLabels"],
  excludePaths: ["technicalGuide.example.encoded"],
  // Key template tuỳ chọn: nhiều trang chưa có trong code (template fallback) → vẫn hiện ô để admin thêm
  optionalTextKeys: ["howItWorksEyebrow", "howItWorksTitle", "howItWorksDescription", "useCasesTitle", "useCasesDescription"]
};

const STATIC_PAGES = [
  { pageKey: "home", label: "Trang chủ", route: "/", contentPath: "home", fieldConfig: HOME_FIELD_CONFIG },
  { pageKey: "contact", label: "Liên hệ", route: "/contact", contentPath: "staticPages.contact", fieldConfig: { fixedPaths: ["topics"] } },
  { pageKey: "pricing", label: "Bảng giá (/service-pack, chỉ EN/VI)", route: "/service-pack", contentPath: "pricing", fieldConfig: { fixedPaths: ["plans", "compareHeaders", "reasons"] } },
  { pageKey: "what-is-qr", label: "QR code là gì", route: "/what-is-qr-code", aliases: ["/qr-code-la-gi"], contentPath: "staticPages.whatIsQr", fieldConfig: { fixedPaths: ["heroStats", "groups", "tocGroups", "dataTags", "createSteps", "correctionLevels", "sections", "mistakes", "qrTags"] } },
  { pageKey: "scan", label: "Quét mã QR (/scan-qr-code)", route: "/scan-qr-code", source: "scan", fieldConfig: { fixedPaths: ["benefits", "onlineGuide.steps", "deviceGuides", "deviceGuides.*.steps", "safety.checks"], excludePaths: ["relatedLinks", "cta.href", "tool.types"] } },
  { pageKey: "help", label: "Trợ giúp (/help)", route: "/help", source: "help", fieldConfig: { fixedPaths: ["topics", "topics.*.checks", "sections"], excludePaths: ["relatedLinks", "ctaHref"] } },
  { pageKey: "blog-list", label: "Danh sách blog", route: "/blog", contentPath: "staticPages.blog", fieldConfig: {} },
  { pageKey: "use-cases-list", label: "Danh sách tình huống sử dụng", route: "/use-cases", contentPath: null, fieldConfig: null },
  { pageKey: "document-list", label: "Tài liệu hướng dẫn (/document)", route: "/document", contentPath: null, fieldConfig: null },
  { pageKey: "legal/privacy-policy", label: "Chính sách bảo mật (chỉ EN)", route: "/privacy-policy", source: "legal", legalKey: "privacy-policy", bodyHtml: true, fieldConfig: {} },
  { pageKey: "legal/term-of-use", label: "Điều khoản sử dụng (chỉ EN)", route: "/term-of-use", source: "legal", legalKey: "term-of-use", bodyHtml: true, fieldConfig: {} }
];

const PRODUCT_PAGE_PREFIX = "products/";
// pageKey sản phẩm = path URL bỏ "/" đầu (vd products/social-media/facebook-qr): luôn chữ thường +
// gạch nối nên hợp lệ với content-service; route.key (camelCase) chỉ dùng cho contentPath.
const productPageKey = (route) => String(route.path || "").replace(/^\/+/, "");

/** Danh sách trang sửa được (tĩnh + sản phẩm theo routes truyền vào). */
function listCmsPages(productRoutes = []) {
  const products = (productRoutes || []).map((route) => ({
    pageKey: productPageKey(route),
    label: `Sản phẩm: ${route.path}`,
    route: route.path,
    contentPath: `pages.${route.key}`,
    fieldConfig: PRODUCT_FIELD_CONFIG
  }));
  return [...STATIC_PAGES, ...products];
}

function getCmsPageDefinition(pageKey, productRoutes = []) {
  return listCmsPages(productRoutes).find((page) => page.pageKey === pageKey) || null;
}

const isPageEditable = (page) => Boolean(page && (page.contentPath || page.source) && page.fieldConfig);

/**
 * path (đã bỏ tiền tố ngôn ngữ, có thể là slug VI) → pageKey. `aliases` = map slug VI → EN
 * (languageRedirects.vi.pathMappings đảo ngược) do caller truyền để module không import gì.
 */
function resolveCmsPageKey(pathname, { productRoutes = [], aliases = {} } = {}) {
  let path = String(pathname || "/").split("?")[0].split("#")[0];
  if (path.length > 1) path = path.replace(/\/+$/, "");
  if (!path) path = "/";
  if (aliases[path]) path = aliases[path];
  for (const page of STATIC_PAGES) {
    if (page.route === path || (page.aliases || []).includes(path)) return page.pageKey;
  }
  const product = (productRoutes || []).find((route) => route.path === path);
  return product ? productPageKey(product) : null;
}

/* ------------------------------------------------------------------ */
/* Snapshot (cmsSiteContent.generated.json)                            */
/* ------------------------------------------------------------------ */

function normalizeCmsLanguage(language) {
  const raw = String(language || "").trim().replace(/_/g, "-");
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower === "zh-tw") return "zh-TW";
  if (lower === "vn") return "vi";
  if (lower === "my") return "mm";
  if (lower === "fil") return "tl";
  return lower.split("-")[0];
}

/** Trang đã publish của một ngôn ngữ trong snapshot: { [pageKey]: { seo, data, content, versionId… } }. */
function getCmsPagesForLanguage(snapshot, language) {
  const pages = snapshot && snapshot.pages;
  if (!pages || typeof pages !== "object") return {};
  const key = normalizeCmsLanguage(language);
  return (key && pages[key]) || {};
}

function getCmsPage(snapshot, language, pageKey) {
  const pages = getCmsPagesForLanguage(snapshot, language);
  return (pageKey && pages[pageKey]) || null;
}

/* ------------------------------------------------------------------ */
/* Merge data override vào content                                     */
/* ------------------------------------------------------------------ */

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const setAtPath = (target, dottedPath, value) => {
  const keys = String(dottedPath).split(".");
  let cursor = target;
  keys.slice(0, -1).forEach((key) => {
    if (!isPlainObject(cursor[key])) cursor[key] = {};
    cursor = cursor[key];
  });
  cursor[keys[keys.length - 1]] = value;
};

const getAtPath = (source, dottedPath) =>
  String(dottedPath).split(".").reduce((cursor, key) => (cursor == null ? undefined : cursor[key]), source);

// So khớp path có wildcard "*" (một đoạn) và bỏ chỉ số mảng: "ecosystem.products.url.facts" ~ "ecosystem.products.*.facts"
const stripIndexes = (path) => String(path).replace(/\[\d*\]/g, "");
const pathMatches = (pattern, path) => {
  const a = String(pattern).split(".");
  const b = stripIndexes(path).split(".");
  if (a.length !== b.length) return false;
  return a.every((seg, i) => seg === "*" || seg === b[i]);
};
const matchesAny = (patterns, path) => (patterns || []).some((pattern) => pathMatches(pattern, path));

/** Đường dẫn (trong content) của các mảng cố định — merge giữ nguyên base nếu lệch độ dài. */
const CMS_FIXED_ARRAY_PATTERNS = STATIC_PAGES.flatMap((page) =>
  page.contentPath && page.fieldConfig
    ? (page.fieldConfig.fixedPaths || []).map((fieldPath) => `${page.contentPath}.${fieldPath}`)
    : []
);
const PRODUCT_FIXED_ARRAY_PATTERNS = PRODUCT_FIELD_CONFIG.fixedPaths.map((p) => `pages.*.${p}`);
const CMS_FIXED_ARRAY_PATHS = new Set([...CMS_FIXED_ARRAY_PATTERNS, ...PRODUCT_FIXED_ARRAY_PATTERNS]);
const isFixedArrayPath = (path, extraPatterns) =>
  matchesAny([...CMS_FIXED_ARRAY_PATHS, ...(extraPatterns || [])], path);

/**
 * Gom override của các trang (một ngôn ngữ) thành object partial cùng hình với content:
 * { home: {...}, staticPages: { contact: {...} }, pages: { dynamic: {...} } }.
 * Chỉ nhận pageKey có contentPath trong registry và có `data`.
 */
function buildCmsContentOverrides(pagesForLanguage, productRoutes = []) {
  const overrides = {};
  let count = 0;
  const definitions = listCmsPages(productRoutes);
  Object.keys(pagesForLanguage || {}).forEach((pageKey) => {
    const entry = pagesForLanguage[pageKey];
    const definition = definitions.find((page) => page.pageKey === pageKey);
    if (!definition || !definition.contentPath || !entry || !isPlainObject(entry.data)) return;
    if (!Object.keys(entry.data).length) return;
    const existing = getAtPath(overrides, definition.contentPath);
    setAtPath(overrides, definition.contentPath, existing ? mergeDeep(existing, entry.data, "") : entry.data);
    count += 1;
  });
  return count ? overrides : null;
}

function mergeDeep(base, override, path, warnings, fixedPatterns) {
  // Phần tử rỗng (''/null) trong mảng override = giữ nguyên base ở vị trí đó
  if (override === undefined || override === null || override === "") return base;
  if (Array.isArray(override)) {
    if (Array.isArray(base) && base.length === override.length) {
      return base.map((item, index) => mergeDeep(item, override[index], `${path}[${index}]`, warnings, fixedPatterns));
    }
    if (Array.isArray(base) && isFixedArrayPath(path, fixedPatterns)) {
      if (warnings) warnings.push(`${path}: mảng cố định ${base.length} phần tử, override có ${override.length} → bỏ qua`);
      return base;
    }
    return override;
  }
  if (isPlainObject(override)) {
    const result = isPlainObject(base) ? { ...base } : {};
    Object.keys(override).forEach((key) => {
      const value = override[key];
      if (value === undefined || value === null || value === "") return;
      result[key] = mergeDeep(result[key], value, path ? `${path}.${key}` : key, warnings, fixedPatterns);
    });
    return result;
  }
  // string/number/boolean: override thắng; chuỗi rỗng = không override (đã lọc ở trên)
  return override;
}

/**
 * Áp override (từ buildCmsContentOverrides) lên content đã dựng xong (sau editorial
 * overrides, localizationCorrections, materialize social/payment). Không mutate input.
 * Trả content mới; `warnings` (nếu truyền) nhận lý do các mảng cố định bị bỏ qua.
 */
function applyCmsContentOverrides(content, overrides, warnings) {
  if (!overrides || !isPlainObject(overrides) || !Object.keys(overrides).length) return content;
  return mergeDeep(content, overrides, "", warnings);
}

/**
 * Module nội dung riêng (scan/help): áp data của pageKey lên object nội dung của module,
 * mảng cố định lấy từ fieldConfig của trang. Không có override → trả đúng object base.
 */
function applyCmsPageData(base, snapshot, language, pageKey, warnings) {
  const page = getCmsPage(snapshot, language, pageKey);
  if (!page || !isPlainObject(page.data) || !Object.keys(page.data).length || !base) return base;
  const definition = STATIC_PAGES.find((item) => item.pageKey === pageKey);
  const fixed = (definition && definition.fieldConfig && definition.fieldConfig.fixedPaths) || [];
  return mergeDeep(base, page.data, "", warnings, fixed);
}

/* ------------------------------------------------------------------ */
/* Thẻ SEO                                                             */
/* ------------------------------------------------------------------ */

/**
 * Ghi đè title/description/keywords/ảnh og của seoData (hình dùng chung worker + Next).
 * `__serverContent.title/description` (SSR fallback + JSON-LD) chỉ đổi khi đang trùng với
 * giá trị SEO cũ, để không đụng H1 lấy từ data.
 */
function applyCmsSeoOverride(seoData, cmsSeo) {
  if (!seoData || !cmsSeo || !isPlainObject(cmsSeo)) return seoData;
  const title = typeof cmsSeo.title === "string" ? cmsSeo.title.trim() : "";
  const description = typeof cmsSeo.description === "string" ? cmsSeo.description.trim() : "";
  const keywords = Array.isArray(cmsSeo.keywords) ? cmsSeo.keywords.map((k) => String(k).trim()).filter(Boolean) : [];
  const image = typeof cmsSeo.ogImage === "string" ? cmsSeo.ogImage.trim() : "";
  if (!title && !description && !keywords.length && !image) return seoData;

  const next = { ...seoData };
  if (title) {
    next.title = title;
    next.og_title = title;
  }
  if (description) next.description = description;
  if (keywords.length) next.keywords = keywords.join(", ");
  if (image) next.image = image;
  if (isPlainObject(seoData.__serverContent)) {
    const serverContent = { ...seoData.__serverContent };
    if (title && serverContent.title === seoData.title) serverContent.title = title;
    if (description && serverContent.description === seoData.description) serverContent.description = description;
    next.__serverContent = serverContent;
  }
  next.__cmsSeoApplied = true;
  return next;
}

/* ------------------------------------------------------------------ */
/* Sinh field cho form admin từ nội dung mặc định                       */
/* ------------------------------------------------------------------ */

const humanizeKey = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

const labelFor = (key, path, labels) => {
  if (labels) {
    const exact = Object.keys(labels).find((pattern) => pathMatches(pattern, path));
    if (exact) return labels[exact];
  }
  return KEY_LABELS[key] || humanizeKey(key);
};

const textFieldFor = (path, key, value, labels, extra = {}) => ({
  path,
  type: value.length > 90 || value.includes("\n") ? "textarea" : "text",
  label: labelFor(key, path, labels),
  ...extra
});

const FIELD_MAX_DEPTH = 5;

/**
 * Sinh field từ subtree nội dung mặc định.
 * - chuỗi → text/textarea; mảng chuỗi → lines (mỗi dòng một mục); mảng object → list/fixedList
 *   với item field (chuỗi hoặc mảng chuỗi lồng); mảng lồng mảng, số, boolean → bỏ.
 * - `group` = nhãn của key cấp 1 để form gom theo khối.
 */
function deriveCmsFields(subtree, config = {}) {
  const fields = [];
  const fixedPaths = config.fixedPaths || [];
  const excludePaths = config.excludePaths || [];
  const labels = config.labels || null;
  const isFixed = (path) => matchesAny(fixedPaths, path);
  const isExcluded = (key, path) => EXCLUDED_KEYS.has(key) || matchesAny(excludePaths, path);

  const itemFieldsFor = (items, path) => {
    const keys = [];
    items.forEach((item) => {
      if (!isPlainObject(item)) return;
      Object.keys(item).forEach((key) => { if (!keys.includes(key)) keys.push(key); });
    });
    const itemFields = [];
    keys.forEach((key) => {
      const itemPath = `${path}[].${key}`;
      if (isExcluded(key, itemPath)) return;
      const holder = items.find((item) => isPlainObject(item) && item[key] !== undefined) || {};
      const value = holder[key];
      if (typeof value === "string") {
        if (isLinkLikeValue(value)) return;
        itemFields.push(textFieldFor(key, key, value, null));
      } else if (Array.isArray(value) && value.length && value.every((v) => typeof v === "string")) {
        itemFields.push({ path: key, type: "lines", label: labelFor(key, itemPath, labels), fixed: isFixed(`${path}.*.${key}`), help: "Mỗi dòng một mục" });
      }
    });
    return itemFields;
  };

  const walk = (node, path, depth, group) => {
    if (depth > FIELD_MAX_DEPTH || !isPlainObject(node)) return;
    Object.keys(node).forEach((key) => {
      const value = node[key];
      const fieldPath = path ? `${path}.${key}` : key;
      if (isExcluded(key, fieldPath)) return;
      const groupLabel = group || labelFor(key, fieldPath, labels);
      if (typeof value === "string") {
        if (isLinkLikeValue(value)) return;
        fields.push(textFieldFor(fieldPath, key, value, labels, { group: group || "Chung" }));
        return;
      }
      if (Array.isArray(value)) {
        if (!value.length) return;
        if (value.every((v) => typeof v === "string")) {
          fields.push({
            path: fieldPath, type: "lines", label: labelFor(key, fieldPath, labels), group: group || groupLabel,
            fixed: isFixed(fieldPath), help: isFixed(fieldPath) ? `Mỗi dòng một mục, giữ đúng ${value.length} dòng (dòng để trống = dùng mặc định)` : "Mỗi dòng một mục"
          });
          return;
        }
        if (value.some((v) => isPlainObject(v))) {
          const item = itemFieldsFor(value, fieldPath);
          if (!item.length) return;
          fields.push({
            path: fieldPath, type: isFixed(fieldPath) ? "fixedList" : "list", label: labelFor(key, fieldPath, labels),
            group: group || groupLabel, item,
            ...(isFixed(fieldPath) ? { help: `Số mục cố định (${value.length}) — chỉ sửa chữ.` } : {})
          });
        }
        return; // mảng lồng mảng / số → bỏ
      }
      if (isPlainObject(value)) walk(value, fieldPath, depth + 1, group || groupLabel);
    });
  };
  walk(subtree, "", 0, "");
  // Key tuỳ chọn chưa có trong nội dung mặc định: thêm ô trống (nhập = thêm mới, web merge thêm key)
  (config.optionalTextKeys || []).forEach((key) => {
    if (fields.some((field) => field.path === key) || isExcluded(key, key)) return;
    if (isPlainObject(subtree) && subtree[key] !== undefined && typeof subtree[key] !== "string") return;
    fields.push({
      path: key,
      type: /description/i.test(key) ? "textarea" : "text",
      label: labelFor(key, key, labels),
      group: "Chung",
      optional: true,
      help: "Chưa có trong code ở ngôn ngữ này — nhập để thêm."
    });
  });
  return fields;
}

/* ------------------------------------------------------------------ */
/* Cho form admin: giá trị mặc định theo fields                        */
/* ------------------------------------------------------------------ */

const pickItem = (item, itemFields) => {
  if (!isPlainObject(item)) return item;
  const picked = {};
  (itemFields || []).forEach((field) => {
    const value = item[field.path];
    if (value !== undefined) picked[field.path] = value;
  });
  return picked;
};

/** Lấy từ subtree content đúng các field (để admin thấy mặc định, không lộ cả JSON). */
function pickCmsFields(subtree, fields) {
  const result = {};
  (fields || []).forEach((field) => {
    const value = getAtPath(subtree, field.path);
    if (value === undefined) return;
    if ((field.type === "list" || field.type === "fixedList") && Array.isArray(value)) {
      setAtPath(result, field.path, value.map((item) => pickItem(item, field.item)));
      return;
    }
    setAtPath(result, field.path, value);
  });
  return result;
}

module.exports = {
  SNAPSHOT_SITE_KEY,
  SNAPSHOT_FILE,
  STATIC_PAGES,
  PRODUCT_PAGE_PREFIX,
  PRODUCT_FIELD_CONFIG,
  CMS_FIXED_ARRAY_PATHS,
  EXCLUDED_KEYS,
  productPageKey,
  listCmsPages,
  getCmsPageDefinition,
  isPageEditable,
  resolveCmsPageKey,
  normalizeCmsLanguage,
  getCmsPagesForLanguage,
  getCmsPage,
  buildCmsContentOverrides,
  applyCmsContentOverrides,
  applyCmsPageData,
  applyCmsSeoOverride,
  deriveCmsFields,
  pickCmsFields,
  getAtPath
};
